import { type Colors } from '@factorialco/deprecated-design-system'
import { ShiftManagementMainPageQuery } from 'generated/resources/graphql'
import { computed, makeObservable } from 'mobx'
import type moment from 'moment'

import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import Holidays from 'modules/shift_management/store/internal/collections/Holidays'
import Leaves from 'modules/shift_management/store/internal/collections/Leaves'
import PlanningVersions from 'modules/shift_management/store/internal/collections/PlanningVersions'
import RestDays from 'modules/shift_management/store/internal/collections/RestDays'
import Shifts from 'modules/shift_management/store/internal/collections/Shifts'
import { PlanningVersionPayload } from 'modules/shift_management/store/internal/models/PlanningVersion'
import { PLANNING_TOOL } from 'modules/time_planning/store/models/PlanningVersion'
import type CompanyHoliday from 'modules/timeoff/store/models/CompanyHoliday'

import { LeavePayload } from './Leave'
import ReferenceContract from './ReferenceContract'
import type RestDay from './RestDay'
import { RestDayPayload } from './RestDay'
import type Shift from './Shift'
import { ShiftPayload } from './Shift'
import ShiftPermissions from './ShiftPermissions'

export type TShiftsRow =
  ShiftManagementMainPageQuery['shiftManagement']['employeesPermissionsConnection']['nodes'][number]

interface Props {
  id: number
  fullName: string
  job: string
  avatar: string
  shifts: Shifts
  leaves: Leaves
  holidays: Holidays
  restDays: RestDays
  planningVersions: PlanningVersions
  permissions: ShiftPermissions
  referenceContract?: ReferenceContract
}

interface RequestAttributes extends Pick<Props, 'id' | 'fullName' | 'job' | 'permissions'> {
  employeeAvatar: string
  shifts: ShiftPayload[]
  leaves: LeavePayload[]
  holidays: CompanyHoliday[]
  restDays: RestDayPayload[]
  planning_versions: PlanningVersionPayload[]
}

class EmployeeWithShifts {
  id: number
  fullName: string
  job: string
  avatar: string
  shifts: Shifts
  leaves: Leaves
  holidays: Holidays
  restDays: RestDays
  planningVersions: PlanningVersions
  permissions: ShiftPermissions
  // TODO: Make this required once 'dev_shift_management_graphql_migration' is done
  referenceContract?: ReferenceContract

  constructor(props: Props) {
    this.id = props.id
    this.fullName = props.fullName
    this.job = props.job
    this.avatar = props.avatar
    this.shifts = props.shifts
    this.leaves = props.leaves
    this.holidays = props.holidays
    this.restDays = props.restDays
    this.planningVersions = props.planningVersions
    this.permissions = props.permissions
    this.referenceContract = props.referenceContract

    makeObservable(this, {
      employeeModel: computed,
      hasDrafts: computed,
      shiftDrafts: computed,
      restDayDrafts: computed,
    })
  }

  get employeeModel(): Employee | undefined {
    return employees.find({ id: this.id })
  }

  /**
   * @deprecated Before using this method, consider using the `buildFromGraphQL` hook since
   * we are trying to move away from using mobx-rest and use GraphQL instead.
   */
  static buildFromRequest(props: RequestAttributes): EmployeeWithShifts {
    return new EmployeeWithShifts({
      id: props.id,
      fullName: props.fullName,
      job: props.job,
      avatar: props.employeeAvatar,
      shifts: Shifts.buildFromRequest(props.shifts),
      leaves: Leaves.buildFromProps(props.leaves),
      holidays: Holidays.buildFromProps(props.holidays),
      restDays: RestDays.buildFromProps(props.restDays),
      planningVersions: PlanningVersions.buildFromProps(props.planning_versions),
      permissions: props.permissions,
    })
  }

  static buildFromGraphQL(shiftsRow: TShiftsRow): EmployeeWithShifts {
    return new EmployeeWithShifts({
      id: shiftsRow.id,
      fullName: shiftsRow.fullName || '',
      job: this.employeeJob(shiftsRow),
      avatar: shiftsRow.avatar?.url || '',
      shifts: Shifts.buildFromGraphQL(shiftsRow.shifts),
      leaves: Leaves.buildFromGraphQL(shiftsRow.leaves),
      holidays: Holidays.buildFromGraphQL(shiftsRow.location),
      restDays: RestDays.buildFromGraphQL(shiftsRow.restDays),
      planningVersions: PlanningVersions.buildFromGraphQL(shiftsRow.planningVersions),
      permissions: ShiftPermissions.buildFromGraphQL(shiftsRow),
      referenceContract: shiftsRow.referenceContract
        ? ReferenceContract.buildFromGraphQL(shiftsRow.referenceContract)
        : undefined,
    })
  }

  static employeeJob(shiftsRow: TShiftsRow): string {
    const { jobTitle, jobCatalogLevel } = shiftsRow.referenceContract || {}

    if (jobTitle) return jobTitle

    const jobRole = jobCatalogLevel?.roleName || ''
    const jobLevel = jobCatalogLevel?.isDefault ? '' : jobCatalogLevel?.name || ''

    return jobRole && jobLevel ? `${jobRole}, ${jobLevel}` : jobRole
  }

  addShifts(shifts: Shift[]): void {
    this.shifts.addShifts(shifts)
  }

  resetShifts(shifts: Shift[]): void {
    this.shifts = new Shifts(shifts)
  }

  addRestDays(restDay: RestDay[]): void {
    this.restDays.addRestDays(restDay)
  }

  destroyRestDay(id: number): void {
    this.restDays.destroyRestDay(id)
  }

  destroyRestDays(ids: number[]): void {
    this.restDays.destroyRestDays(ids)
  }

  destroyShift(id: number): void {
    this.shifts.destroyShift(id)
  }

  destroyShifts(ids: number[]): void {
    this.shifts = this.shifts.destroyShifts(ids)
  }

  get restDayDrafts(): RestDay[] {
    return this.restDays.filterByDraft()
  }

  get shiftDrafts(): Shift[] {
    return this.shifts.filterByDraft()
  }

  get hasDrafts(): boolean {
    return !!this.restDayDrafts.length || !!this.shiftDrafts.length
  }

  get canManage(): boolean {
    return !!this.permissions.create
  }

  get canCreateLeaves(): boolean {
    return !!this.permissions.createLeave
  }

  get canApproveLeaves(): boolean {
    return !!this.permissions.approveLeave
  }

  get canDeleteLeaves(): boolean {
    return !!this.permissions.deleteLeave
  }

  get hasNoneShiftManagementPlanning(): boolean {
    return this.planningVersions.filterByPlanningTool(PLANNING_TOOL.shiftManagement).length === 0
  }

  visibleShifts(): Shift[] {
    const { shifts } = this.shifts
    return shifts.filter((shift: Shift) => {
      const employeeModel = this.employeeModel
      const planningVersion = this.planningVersions.planningVersionWithinDate(shift.date)

      if (!employeeModel) return true

      return (
        planningVersion?.isShiftManagementTool &&
        (!employeeModel.isTerminated || shift.date <= employeeModel.terminationDate!)
      )
    })
  }

  canManageOn(date: moment.Moment): boolean | undefined {
    return (
      this.canManage && this.planningVersions.planningVersionWithinDate(date)?.isShiftManagementTool
    )
  }

  updateShiftsColor(ids: number[], color: Colors): EmployeeWithShifts {
    const shifts = this.shifts.updateShiftsColor(ids, color)
    return new EmployeeWithShifts({ ...this, shifts })
  }
}

export default EmployeeWithShifts
